
import React, { useState, useEffect } from "react";
import TopBar from "../../common/TopBar";
import Sidebar from "../../common/Sidebar";
import { DataGrid } from "@mui/x-data-grid";
import { ToastContainer, toast } from "react-toastify";
import {
  Button,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import { FiEdit, FiTrash2, FiPlus } from "react-icons/fi";
import {
  getTopThingsToDoList,
  createTopThingsToDoList,
  handleTopThingsToDoList,
  handleDeleteTopThingsToDoById, // Update API
  fetchAllTourPlaces
} from "../../../services/adminRestapiService";
import { getImageUploadError, IMAGE_UPLOAD_ACCEPT, IMAGE_UPLOAD_HELP } from "../../../utils/imageUpload";

function PlacesVisited() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [topThings, setTopThings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryList, setCategoryList] = useState([]);
  // Add modal
  const [openAddModal, setOpenAddModal] = useState(false);
  const [newTopThing, setNewTopThing] = useState({
    title: "",
    category: "",
    description: "",
    image: null,
    actualprice: "",
    discountedprice: "",
    created_by: 1,
  });

  // Edit modal
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editTopThing, setEditTopThing] = useState({
    id: "",
    title: "",
    category: "",
    description: "",
    image: null,
    actualprice: "",
    discountedprice: "",
    created_by: 1,
  });
  const [editImageFile, setEditImageFile] = useState(null);
  const toggleSidebar = () => setSidebarOpen((isCurrentlyOpen) => !isCurrentlyOpen);
  const [tourPlaces, setTourPlaces] = useState([]);

  // Fetch list
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await getTopThingsToDoList(token);
        const topThingsData = response.data.data.map((item) => ({
          ...item,
          id: item.id,
        }));
        setTopThings(topThingsData);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to fetch top things");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);


  useEffect(() => {
    loadPlaces();
  }, []);

  const loadPlaces = async () => {
    try {
      const res = await fetchAllTourPlaces();
      const data = res.data.data;

      // Save entire data for table
      setTourPlaces(data);

      // Extract unique categories for dropdown
      const uniqueCategories = [...new Set(data.map(item => item.category))];
      setCategoryList(uniqueCategories);

    } catch (error) {
      toast.error("Failed to fetch display titles");
    } finally {
      setLoading(false);
    }
  }

  // Input handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewTopThing({ ...newTopThing, [name]: value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const imageError = getImageUploadError(file);
    if (imageError) {
      toast.error(imageError);
      e.target.value = "";
      return;
    }
    if (file) {
      setNewTopThing({ ...newTopThing, image: file });
    }
  };

  const handleAddSubmit = async () => {
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();

      formData.append("title", newTopThing.title);
      formData.append("category", newTopThing.category);
      formData.append("description", newTopThing.description);
      formData.append("actual_price", newTopThing.actualprice);
      formData.append("discounted_price", newTopThing.discountedprice);
      formData.append("created_by", newTopThing.created_by);

      if (newTopThing.image) formData.append("image", newTopThing.image);

      await createTopThingsToDoList(token, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const response = await getTopThingsToDoList(token);
      setTopThings(
        response.data.data.map((item) => ({ ...item, id: item.id }))
      );

      setOpenAddModal(false);
      setNewTopThing({
        title: "",
        category: "",
        description: "",
        image: null,
        actualprice: "",
        discountedprice: "",
        created_by: 1,
      });

      // ✅ Show success toast
      toast.success("Top thing added successfully!");
    } catch (err) {
      console.error("Failed to create top thing:", err);

      // ✅ Show error toast
      toast.error(err.response?.data?.message || "Failed to add top thing");
    }
  };
  // Edit handlers
  const handleEditClick = (item) => {
    setEditTopThing({
      id: item.id,
      title: item.title,
      category: item.category,
      description: item.description,
      image: item.image,
      actualprice: item.actual_price,          // ✅ mapping
      discountedprice: item.discounted_price,  // ✅ mapping
      created_by: item.created_by,
    });

    setEditImageFile(null);
    setOpenEditModal(true);
  };


  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditTopThing({ ...editTopThing, [name]: value });
  };

  const handleEditFileChange = (e) => {
    const file = e.target.files[0];
    const imageError = getImageUploadError(file);
    if (imageError) {
      toast.error(imageError);
      e.target.value = "";
      return;
    }
    if (file) {
      setEditImageFile(file);
    }
  };

  const handleEditSubmit = async () => {
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();

      formData.append("title", editTopThing.title);
      formData.append("category", editTopThing.category);
      formData.append("description", editTopThing.description);
      formData.append("actual_price", editTopThing.actualprice);
      formData.append("discounted_price", editTopThing.discountedprice);
      formData.append("created_by", editTopThing.created_by);

      // Only append new file if selected
      // if (editImageFile) formData.append("image", editImageFile);
      if (editImageFile) {
        formData.append("image", editImageFile);
      } else if (editTopThing.image) {
        formData.append("image", editTopThing.image); // send existing path
      }

      // Call update API
      await handleTopThingsToDoList(token, editTopThing.id, formData);

      // Refresh list
      const response = await getTopThingsToDoList(token);
      setTopThings(
        response.data.data.map((item) => ({ ...item, id: item.id }))
      );

      setOpenEditModal(false);
      setEditTopThing({
        id: "",
        title: "",
        category: "",
        description: "",
        image: null,
        actualprice: "",
        discountedprice: "",
        created_by: 1,
      });
      setEditImageFile(null);

      // ✅ Show success toast
      toast.success("Top Thing updated successfully!");
    } catch (err) {
      console.error("Failed to edit top thing:", err);

      // ✅ Show error toast
      const errorMessage =
        err.response?.data?.message || "Failed to edit top thing";
      toast.error(errorMessage);
    }
  };

  const handleDeleteClick = async (id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;

    try {
      const token = localStorage.getItem("token");
      await handleDeleteTopThingsToDoById(token, id);

      // Remove deleted item from state
      setTopThings((prev) => prev.filter((item) => item.id !== id));

      toast.success("Deleted successfully!");
    } catch (err) {
      console.error("Failed to delete top thing:", err);
      toast.error(err.response?.data?.message || "Failed to delete top thing");
    }
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5 },
    { field: "title", headerName: "Title", flex: 2 },
    { field: "category", headerName: "Place", flex: 1.5 },
    { field: "description", headerName: "Description", flex: 3 },

    { field: "actual_price", headerName: "Actual Price (₹)", flex: 1 },
    { field: "discounted_price", headerName: "Discounted Price (₹)", flex: 1 },
    {
      field: "image",
      headerName: "Image",
      flex: 1.5,
      renderCell: (params) => {
        const baseURL = "https://api.gosehari.com";
        const imageURL = params.value
          ? `${baseURL}${params.value}`
          : "/placeholder.png";
        return (
          <img
            src={imageURL}
            alt={params.row.title}
            style={{
              width: "100%",
              height: "60px",
              padding: "10px",
              objectFit: "cover",
              borderRadius: "4px",
            }}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1.5,
      renderCell: (params) => (
        <Box className="flex gap-2">
          <IconButton
            color="primary"
            size="small"
            onClick={() => handleEditClick(params.row)}
          >
            <FiEdit />
          </IconButton>
          <IconButton
            color="error"
            size="small"
            onClick={() => handleDeleteClick(params.row.id)}
          >
            <FiTrash2 />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <div className="flex min-h-screen bg-slate-50">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        pauseOnHover
        draggable
        theme="colored"
      />
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div
        className={`min-h-screen min-w-0 flex-1 transition-[margin] duration-300 ${sidebarOpen ? "lg:ml-64" : "lg:ml-20"
          }`}
      >
        <TopBar toggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} />
        <main className="px-4 pb-8 pt-24 sm:px-6 lg:px-8">
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-xl font-semibold text-slate-900 sm:text-2xl">All Places</h2>
            <Button
              variant="contained"
              color="black"
              onClick={() => setOpenAddModal(true)}
              sx={{
                borderRadius: "50%", // makes it circular
                minWidth: "40px",
                minHeight: "40px",
                padding: 0,
              }}
            >
              <FiPlus size={20} /> {/* icon size */}
            </Button>
          </div>
          {error && <p className="text-red-500 mb-4">{error}</p>}
          <div
            style={{
              width: "100%",
              borderRadius: "10px", // Rounded corners
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)", // Light shadow
              overflow: "hidden", // Ensures DataGrid corners are clipped
            }}
          >
            <DataGrid
              rows={topThings}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 20, 50]}
              loading={loading}
              disableSelectionOnClick
              autoHeight
            />
          </div>

          {/* Add Modal */}
          <Dialog
            open={openAddModal}
            onClose={() => setOpenAddModal(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>
              <Typography
                variant="h6"
                align="center"
                style={{ fontWeight: "bold" }}
              >
                Add Top Thing To Do
              </Typography>
            </DialogTitle>
            <DialogContent className="space-y-4 mt-2">
              <input
                type="text"
                name="title"
                placeholder="Title"
                value={newTopThing.title}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded"
              />

              <select
                name="category"
                value={newTopThing.category}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded"
              >
                <option value="">Select Category</option>
                {categoryList.map((cat, index) => (
                  <option key={index} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>

              <input
                type="text"
                name="description"
                placeholder="Description"
                value={newTopThing.description}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded"
              />
              <input
                type="number"
                name="actualprice"
                placeholder="Actual Price"
                value={newTopThing.actualprice}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded"
              />
              <input
                type="number"
                name="discountedprice"
                placeholder="Discounted Price"
                value={newTopThing.discountedprice}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded"
              />
              <input type="file" accept={IMAGE_UPLOAD_ACCEPT} onChange={handleFileChange} />
              <p className="mt-1 text-xs text-gray-500">{IMAGE_UPLOAD_HELP}</p>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenAddModal(false)}>Cancel</Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddSubmit}
              >
                Add
              </Button>
            </DialogActions>
          </Dialog>

          {/* Edit Modal */}
          <Dialog
            open={openEditModal}
            onClose={() => setOpenEditModal(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>
              <Typography
                variant="h6"
                align="center"
                style={{ fontWeight: "bold" }}
              >
                Edit Top Thing To Do
              </Typography>
            </DialogTitle>
            <DialogContent className="space-y-4 mt-2">
              <input
                type="text"
                name="title"
                placeholder="Title"
                value={editTopThing.title}
                onChange={handleEditInputChange}
                className="w-full px-3 py-2 border rounded"
              />
              <input
                type="text"
                name="category"
                placeholder="Place"
                value={editTopThing.category}
                onChange={handleEditInputChange}
                className="w-full px-3 py-2 border rounded"
              />
              <input
                type="text"
                name="description"
                placeholder="Description"
                value={editTopThing.description}
                onChange={handleEditInputChange}
                className="w-full px-3 py-2 border rounded"
              />
              <input
                type="number"
                name="actualprice"
                placeholder="Actual Price"
                value={editTopThing.actualprice}
                onChange={handleEditInputChange}
                className="w-full px-3 py-2 border rounded"
              />
              <input
                type="number"
                name="discountedprice"
                placeholder="Discounted Price"
                value={editTopThing.discountedprice}
                onChange={handleEditInputChange}
                className="w-full px-3 py-2 border rounded"
              />
              <input
                type="file"
                accept={IMAGE_UPLOAD_ACCEPT}
                onChange={handleEditFileChange}
              />
              <p className="mt-1 text-xs text-gray-500">{IMAGE_UPLOAD_HELP}</p>
              {editTopThing.image && !editImageFile && (
                <img
                  src={`https://api.gosehari.com${editTopThing.image}`}
                  alt="Existing"
                  style={{
                    width: "100%",
                    height: "120px",
                    objectFit: "cover",
                    marginTop: "8px",
                    borderRadius: "4px",
                  }}
                />
              )}

            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenEditModal(false)}>Cancel</Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleEditSubmit}
              >
                Update
              </Button>
            </DialogActions>
          </Dialog>
        </main>
      </div>
    </div>
  );
}

export default PlacesVisited;
